import { generateAccessName, AccessKey } from "../../../../access";

/**
 * Base keys of the access sections on this page.
 */
const ACCESS_SECTION: AccessKey[] = [
	AccessKey.ORDERS,
	AccessKey.WIDGET_ORDER_EXECUTORS,
];

const ACCESS_SECTION_FILTER: AccessKey[] = [
	AccessKey.ORDERS,
	AccessKey.WIDGET_ORDER_EXECUTORS,
	AccessKey.FILTER,
];

enum TabKeys {
	CHANGE_PERFORMERS_STATUS = AccessKey.CHANGE_PERFORMERS_STATUS,
	CHANGE_PERFORMERS_STATUS_SELECTED = AccessKey.CHANGE_PERFORMERS_STATUS_SELECTED,
	CALL_ORDER_EXECUTOR = AccessKey.CALL_ORDER_EXECUTOR,
	SEARCH = AccessKey.SEARCH,
	FILTER = AccessKey.FILTER,
	LOWERS_STATISTICS_TABLE = AccessKey.LOWERS_STATISTICS_TABLE,
	//
	COMPANIES_AND_TAXI_SERVICES = AccessKey.COMPANIES_AND_TAXI_SERVICES,
	CAR_PARKS = AccessKey.CAR_PARKS,
	EXECUTOR_TEAMS = AccessKey.EXECUTOR_TEAMS,
	STATUS = AccessKey.STATUS,
	IS_WORKING = AccessKey.IS_WORKING,
}

/**
 * Constructs an array of access addresses by combining base access addresses
 * with the tab-specific access keys. This builds the full set of addresses
 * needed to manage access from the page level to the specific tab.
 *
 * @param key - Tab-specific access keys.
 * @returns Array of access addresses including base and tab-specific addresses.
 */
const generateAccessSection = (key: AccessKey): AccessKey[] => [
	...ACCESS_SECTION,
	key,
];
const generateAccessSectionFilter = (key: AccessKey): AccessKey[] => [
	...ACCESS_SECTION_FILTER,
	key,
];

/**
 * Maps each tab on the page to its corresponding array of access addresses.
 * This container is used for managing access control not only for the tabs themselves,
 * but also for internal elements such as buttons within these tabs.
 * Each entry provides a set of access addresses specific to the tab.
 */
const ARR_ACCESS_PART_KEY: Record<TabKeys, AccessKey[]> = {
	[TabKeys.CHANGE_PERFORMERS_STATUS]: generateAccessSection(
		AccessKey.CHANGE_PERFORMERS_STATUS,
	),
	[TabKeys.CHANGE_PERFORMERS_STATUS_SELECTED]: generateAccessSection(
		AccessKey.CHANGE_PERFORMERS_STATUS_SELECTED,
	),
	[TabKeys.CALL_ORDER_EXECUTOR]: generateAccessSection(
		AccessKey.CALL_ORDER_EXECUTOR,
	),
	[TabKeys.SEARCH]: generateAccessSection(AccessKey.SEARCH),
	[TabKeys.FILTER]: generateAccessSection(AccessKey.FILTER),
	[TabKeys.LOWERS_STATISTICS_TABLE]: generateAccessSection(
		AccessKey.LOWERS_STATISTICS_TABLE,
	),
	//
	[TabKeys.COMPANIES_AND_TAXI_SERVICES]: generateAccessSectionFilter(
		AccessKey.COMPANIES_AND_TAXI_SERVICES,
	),
	[TabKeys.CAR_PARKS]: generateAccessSectionFilter(AccessKey.CAR_PARKS),
	[TabKeys.EXECUTOR_TEAMS]: generateAccessSectionFilter(
		AccessKey.EXECUTOR_TEAMS,
	),
	[TabKeys.STATUS]: generateAccessSectionFilter(AccessKey.STATUS),
	[TabKeys.IS_WORKING]: generateAccessSectionFilter(AccessKey.IS_WORKING),
};

/**
 * Type representing the access names for each tab.
 */
type TabAccessNamesType = {
	[key in TabKeys]: string;
};

/**
 * Generates access names from arrays of access keys for each tab.
 * This mapping creates a unique access name for each tab, which is used
 * to determine whether the tab is accessible or not. Each access name represents
 * the combined access keys for the tab.
 *
 * @returns TabAccessNamesType - An object mapping each tab key to its unique access name
 */
const TabAccessNames: TabAccessNamesType = Object.fromEntries(
	Object.entries(ARR_ACCESS_PART_KEY).map(([key, section]) => [
		key,
		generateAccessName(...section),
	]),
) as TabAccessNamesType;

export { TabKeys, TabAccessNames, ARR_ACCESS_PART_KEY };
